import React, { useState, useEffect, useRef } from 'react';
import { Wrench, Clock, ExternalLink, MessageCircle, Mail, Shield, Activity, CheckCircle2, AlertTriangle, XCircle, ChevronRight, Globe } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MaintenancePage = ({ title, message, scheduledEnd }) => {
  const [timeLeft, setTimeLeft] = useState({ h: 0, m: 0, s: 0, text: '' });
  const [progress, setProgress] = useState(0);
  const [statusData, setStatusData] = useState(null);
  const [startTime] = useState(() => Date.now());
  const canvasRef = useRef(null);

  // Countdown timer
  useEffect(() => {
    if (!scheduledEnd) return;
    const endTime = new Date(scheduledEnd).getTime();
    const totalDuration = endTime - startTime;

    const tick = () => {
      const now = Date.now();
      const diff = endTime - now;
      if (diff <= 0) {
        setTimeLeft({ h: 0, m: 0, s: 0, text: 'Coming back now...' });
        setProgress(100);
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft({ h, m, s, text: '' });

      if (totalDuration > 0) {
        const elapsed = now - startTime;
        setProgress(Math.min(Math.round((elapsed / totalDuration) * 100), 99));
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [scheduledEnd, startTime]);

  // Fetch system status
  useEffect(() => {
    const fetchStatus = () => {
      axios.get(`${BACKEND_URL}/api/maintenance/status`).then(r => setStatusData(r.data)).catch(() => {});
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 15000);
    return () => clearInterval(interval);
  }, []);

  // Particle animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 2 + 0.5,
        dx: (Math.random() - 0.5) * 0.3,
        dy: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.4 + 0.1,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 229, 255, ${p.opacity})`;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });
      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dist = Math.hypot(particles[i].x - particles[j].x, particles[i].y - particles[j].y);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 229, 255, ${0.04 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  const SERVICES = [
    { name: 'Website', key: 'website' },
    { name: 'Staff Portal', key: 'staff_portal' },
    { name: 'Live Chat', key: 'live_chat' },
    { name: 'API Services', key: 'api' },
  ];

  const getServiceStatus = (key) => {
    if (!statusData) return 'checking';
    if (statusData.overall_status === 'maintenance') return key === 'api' ? 'operational' : 'maintenance';
    return 'operational';
  };

  const STATUS_DISPLAY = {
    operational: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10', dot: 'bg-emerald-500', label: 'Operational' },
    maintenance: { icon: Wrench, color: 'text-cyan-400', bg: 'bg-cyan-500/10', dot: 'bg-cyan-500', label: 'Maintenance' },
    degraded: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10', dot: 'bg-amber-500', label: 'Degraded' },
    down: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', dot: 'bg-red-500', label: 'Down' },
    checking: { icon: Activity, color: 'text-slate-400', bg: 'bg-slate-500/10', dot: 'bg-slate-500', label: 'Checking...' },
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] relative overflow-hidden" data-testid="maintenance-page">
      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />

      {/* Ambient glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-500/3 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="border-b border-white/[0.04] bg-white/[0.01] backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_209c8813-566e-47da-85c4-1d1fadce8833/artifacts/rzf7k7uf_starnight101.png"
                alt="Starnight Holdings" className="h-7 w-auto object-contain opacity-60"
              />
            </div>
            <a href="/system-status" data-testid="maintenance-status-link"
              className="flex items-center gap-1.5 text-cyan-400/70 hover:text-cyan-400 text-xs font-medium transition-colors">
              <Activity className="w-3.5 h-3.5" /> System Status <ChevronRight className="w-3 h-3" />
            </a>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="max-w-2xl w-full">
            {/* Hero */}
            <div className="text-center mb-10">
              <div className="relative w-24 h-24 mx-auto mb-8">
                <div className="absolute inset-0 rounded-full bg-cyan-500/10 border border-cyan-500/20 animate-pulse" />
                <div className="absolute inset-2 rounded-full bg-cyan-500/5 border border-cyan-500/10 flex items-center justify-center">
                  <Wrench className="w-10 h-10 text-cyan-400" />
                </div>
                {/* Orbit ring */}
                <div className="absolute inset-[-8px] rounded-full border border-dashed border-cyan-500/10 animate-[spin_20s_linear_infinite]" />
              </div>

              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight" data-testid="maintenance-title">
                {title || 'Under Maintenance'}
              </h1>
              <p className="text-slate-400 text-base sm:text-lg leading-relaxed max-w-lg mx-auto" data-testid="maintenance-message">
                {message || "We're performing scheduled maintenance to improve your experience. We'll be back shortly."}
              </p>
            </div>

            {/* Countdown */}
            {scheduledEnd && (
              <div className="mb-10">
                {timeLeft.text ? (
                  <div className="text-center">
                    <p className="text-cyan-400 text-xl font-semibold animate-pulse">{timeLeft.text}</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-3 mb-4" data-testid="maintenance-countdown">
                      <CountdownBox value={timeLeft.h} label="Hours" />
                      <span className="text-cyan-500/30 text-3xl font-light mt-[-16px]">:</span>
                      <CountdownBox value={timeLeft.m} label="Minutes" />
                      <span className="text-cyan-500/30 text-3xl font-light mt-[-16px]">:</span>
                      <CountdownBox value={timeLeft.s} label="Seconds" />
                    </div>

                    {/* Progress bar */}
                    <div className="max-w-sm mx-auto">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Progress</span>
                        <span className="text-[10px] text-cyan-400 font-mono font-bold">{progress}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-800/50 rounded-full overflow-hidden border border-white/[0.04]" data-testid="maintenance-progress">
                        <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full transition-all duration-1000 relative"
                          style={{ width: `${progress}%` }}>
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_2s_infinite]" />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Service Status Grid */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white/60 text-xs uppercase tracking-[0.15em] font-medium">Service Status</h2>
                <span className="text-[10px] text-slate-600 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-pulse" /> Auto-refreshing
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="maintenance-services">
                {SERVICES.map(service => {
                  const status = getServiceStatus(service.key);
                  const display = STATUS_DISPLAY[status];
                  const Icon = display.icon;
                  return (
                    <div key={service.key}
                      className={`${display.bg} border border-white/[0.06] rounded-xl p-3.5 text-center transition-all hover:border-white/10`}>
                      <Icon className={`w-5 h-5 ${display.color} mx-auto mb-2`} />
                      <p className="text-white/70 text-xs font-medium mb-1">{service.name}</p>
                      <div className="flex items-center justify-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${display.dot}`} />
                        <span className={`text-[10px] ${display.color} font-medium`}>{display.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Active Incidents */}
            {statusData?.active_incidents?.length > 0 && (
              <div className="mb-10">
                <h2 className="text-white/60 text-xs uppercase tracking-[0.15em] font-medium mb-3">Active Incidents</h2>
                <div className="space-y-2">
                  {statusData.active_incidents.map((inc, i) => (
                    <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 flex items-start gap-3">
                      <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white/80 text-sm font-medium">{inc.title}</p>
                        {inc.description && <p className="text-slate-500 text-xs mt-1">{inc.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Links */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="/system-status" data-testid="maintenance-detailed-status"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm font-medium hover:bg-cyan-500/20 transition-all">
                <Shield className="w-4 h-4" /> Detailed Status Page <ExternalLink className="w-3.5 h-3.5 opacity-50" />
              </a>
              <a href="https://discord.gg/2PFMA3hKjW" target="_blank" rel="noopener noreferrer" data-testid="maintenance-discord"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-white/50 text-sm font-medium hover:bg-white/[0.06] hover:text-white/70 transition-all">
                <MessageCircle className="w-4 h-4" /> Join Discord for Updates
              </a>
              <a href="mailto:help@starnightholdings.uk" data-testid="maintenance-email"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-white/50 text-sm font-medium hover:bg-white/[0.06] hover:text-white/70 transition-all">
                <Mail className="w-4 h-4" /> Contact Us
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-white/[0.04] bg-white/[0.01]">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <p className="text-slate-600 text-[11px]">&copy; {new Date().getFullYear()} Starnight Holdings</p>
            <div className="flex items-center gap-4">
              <a href="mailto:help@starnightholdings.uk" className="text-slate-600 hover:text-slate-400 text-[11px] transition-colors">
                help@starnightholdings.uk
              </a>
              <a href="https://discord.gg/2PFMA3hKjW" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-slate-400 transition-colors">
                <MessageCircle className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

// Countdown digit box
const CountdownBox = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="w-[72px] h-[80px] bg-[#0d1117] border border-white/[0.08] rounded-xl flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-1/2 bg-white/[0.02]" />
      <span className="text-3xl font-mono font-bold text-cyan-400 tabular-nums" data-testid={`countdown-${label.toLowerCase()}`}>
        {String(value).padStart(2, '0')}
      </span>
    </div>
    <span className="text-[10px] text-slate-600 mt-1.5 uppercase tracking-wider font-medium">{label}</span>
  </div>
);

export default MaintenancePage;
